import React, { useState } from 'react';
import { Typography, Button, Space } from 'antd';
import { 
  CopyOutlined, 
  ExclamationCircleOutlined,
  DownOutlined,
  RightOutlined
} from '@ant-design/icons';
import { copyToClipboard } from '../utils';

const ErrorDisplay = ({ error }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!error) return null;

  const errorMessage = error.message || 'An unknown error occurred';
  const hasDetails = error.details || error.code;

  // Parse common error patterns to provide user-friendly messages
  const getUserFriendlyMessage = (msg) => {
    if (!msg) return 'The job failed to complete.';
    
    const lowerMsg = msg.toLowerCase();
    
    if (lowerMsg.includes('timeout')) {
      return 'The request timed out. This might be due to high server load. Please try again.';
    }
    if (lowerMsg.includes('nsfw') || lowerMsg.includes('content policy')) {
      return 'The content was flagged by our safety filters. Please modify your input and try again.';
    }
    if (lowerMsg.includes('rate limit')) {
      return 'Rate limit exceeded. Please wait a moment before trying again.';
    }
    if (lowerMsg.includes('invalid') && lowerMsg.includes('image')) {
      return 'The input image format is not supported or the image is corrupted.';
    }
    if (lowerMsg.includes('out of memory') || lowerMsg.includes('oom')) {
      return 'The generation was too complex. Try reducing image size or simplifying your request.';
    }
    if (lowerMsg.includes('network') || lowerMsg.includes('connection')) {
      return 'A network error occurred. Please check your connection and try again.';
    }
    
    return msg;
  };

  const friendlyMessage = getUserFriendlyMessage(errorMessage);

  return (
    <div className="jdv-error-display">
      <div className="jdv-error-icon">
        <ExclamationCircleOutlined />
      </div>
      
      <div className="jdv-error-content">
        <Typography.Title level={5} className="jdv-error-title">
          Job Failed
        </Typography.Title>
        
        <Typography.Paragraph className="jdv-error-message">
          {friendlyMessage}
        </Typography.Paragraph>
        
        {/* Technical details */}
        {hasDetails && (
          <div className="jdv-error-details">
            <button 
              className="jdv-error-toggle"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? <DownOutlined /> : <RightOutlined />}
              Technical Details
            </button>
            
            {showDetails && (
              <div className="jdv-error-technical">
                {error.code && (
                  <div className="jdv-error-code">
                    <span className="label">Error Code:</span>
                    <code>{error.code}</code>
                  </div>
                )}
                
                {error.message && error.message !== friendlyMessage && (
                  <div className="jdv-error-raw">
                    <span className="label">Raw Message:</span>
                    <code>{error.message}</code>
                  </div>
                )}
                
                {error.details && (
                  <div className="jdv-error-stack">
                    <span className="label">Details:</span>
                    <pre>
                      {typeof error.details === 'string' 
                        ? error.details 
                        : JSON.stringify(error.details, null, 2)
                      }
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Actions */}
        <div className="jdv-error-actions">
          <Space size={8}>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(JSON.stringify(error, null, 2))}
            >
              Copy Error
            </Button>
          </Space>
        </div>
      </div>
    </div>
  );
};

export default ErrorDisplay;
